/**
 * Canonical list of workshop specialties.
 * Single source of truth — all apps import from here.
 *
 * `WORKSHOP_SPECIALTY_KEYS` is declared as a literal tuple on purpose, mirroring
 * `service-categories.ts`: `z.enum` infers the union from it, so a value the API
 * validates stays assignable to `WorkshopSpecialtyKey`. Deriving the tuple with
 * `.map()` widens it to `string[]` and forces a cast at every Zod boundary.
 *
 * Cada chave pertence a um **segmento** (`WorkshopSegment`). O segmento é o que
 * o produto usa para contar quantas oficinas da base já operam fora da mecânica
 * geral — e, quando os módulos verticais estrearem, é dele que sai o default de
 * qual módulo sugerir. Ver `docs/ref/mustauto_modulos_verticais_plano.md`.
 */

/** Segmento de atuação da oficina. Uma oficina pode estar em mais de um. */
export const WORKSHOP_SEGMENT_KEYS = ['mecanica', 'funilaria', 'estetica'] as const;

export type WorkshopSegment = (typeof WORKSHOP_SEGMENT_KEYS)[number];

export const WORKSHOP_SEGMENT_LABELS: Record<WorkshopSegment, string> = {
  mecanica: 'Mecânica',
  funilaria: 'Funilaria e pintura',
  estetica: 'Estética automotiva',
};

export const WORKSHOP_SPECIALTY_KEYS = [
  // ── Mecânica ────────────────────────────────────────────────────────
  'engine',
  'suspension',
  'brakes',
  'electrical',
  'fuel_injection',
  'air_conditioning',
  'transmission',
  'alignment',
  'balancing',
  'tires',
  'performance',
  'general',
  // ── Funilaria e pintura ─────────────────────────────────────────────
  'bodywork',
  'painting',
  'paintless_dent',
  // ── Estética automotiva ─────────────────────────────────────────────
  'detailing',
  'ceramic_coating',
  'ppf',
  'window_tint',
  'upholstery',
] as const;

export type WorkshopSpecialtyKey = (typeof WORKSHOP_SPECIALTY_KEYS)[number];

/** Rótulos PT-BR. O `Record` obriga a cobrir toda chave nova em tempo de compilação. */
const WORKSHOP_SPECIALTY_LABELS: Record<WorkshopSpecialtyKey, string> = {
  engine: 'Motor',
  suspension: 'Suspensão',
  brakes: 'Freios',
  electrical: 'Elétrica',
  fuel_injection: 'Injeção Eletrônica',
  air_conditioning: 'Ar Condicionado',
  transmission: 'Câmbio',
  alignment: 'Alinhamento',
  balancing: 'Balanceamento',
  tires: 'Pneus',
  performance: 'Performance',
  general: 'Mecânica Geral',

  bodywork: 'Funilaria',
  painting: 'Pintura',
  paintless_dent: 'Martelinho de Ouro',

  detailing: 'Estética e Polimento',
  ceramic_coating: 'Vitrificação',
  ppf: 'Película PPF',
  window_tint: 'Insulfilm',
  upholstery: 'Higienização e Estofados',
};

/** Segmento de cada especialidade. O `Record` também é exaustivo por construção. */
const WORKSHOP_SPECIALTY_SEGMENTS: Record<WorkshopSpecialtyKey, WorkshopSegment> = {
  engine: 'mecanica',
  suspension: 'mecanica',
  brakes: 'mecanica',
  electrical: 'mecanica',
  fuel_injection: 'mecanica',
  air_conditioning: 'mecanica',
  transmission: 'mecanica',
  alignment: 'mecanica',
  balancing: 'mecanica',
  tires: 'mecanica',
  performance: 'mecanica',
  general: 'mecanica',

  bodywork: 'funilaria',
  painting: 'funilaria',
  paintless_dent: 'funilaria',

  detailing: 'estetica',
  ceramic_coating: 'estetica',
  ppf: 'estetica',
  window_tint: 'estetica',
  upholstery: 'estetica',
};

export const WORKSHOP_SPECIALTIES: readonly {
  key: WorkshopSpecialtyKey;
  label: string;
  segment: WorkshopSegment;
}[] = WORKSHOP_SPECIALTY_KEYS.map((key) => ({
  key,
  label: WORKSHOP_SPECIALTY_LABELS[key],
  segment: WORKSHOP_SPECIALTY_SEGMENTS[key],
}));

export function getSpecialtyLabel(key: string): string {
  return WORKSHOP_SPECIALTY_LABELS[key as WorkshopSpecialtyKey] ?? key;
}

export function isValidSpecialtyKey(key: string): key is WorkshopSpecialtyKey {
  return (WORKSHOP_SPECIALTY_KEYS as readonly string[]).includes(key);
}

export function getSegmentLabel(key: string): string {
  return WORKSHOP_SEGMENT_LABELS[key as WorkshopSegment] ?? key;
}

/**
 * Segmentos que um conjunto de especialidades declara, na ordem de
 * `WORKSHOP_SEGMENT_KEYS`. Chave desconhecida (dado antigo ou digitado à mão)
 * é ignorada em vez de virar segmento — a lista acima é a fonte de verdade.
 *
 * Lista vazia significa "não declarou", que é diferente de "é mecânica": a
 * contagem da base precisa saber distinguir os dois.
 */
export function segmentsFromSpecialties(specialties: readonly string[]): WorkshopSegment[] {
  const found = new Set<WorkshopSegment>();
  for (const key of specialties) {
    const segment = WORKSHOP_SPECIALTY_SEGMENTS[key as WorkshopSpecialtyKey];
    if (segment) found.add(segment);
  }
  return WORKSHOP_SEGMENT_KEYS.filter((s) => found.has(s));
}
